import { AfterContentInit, ContentChildren, Directive, ElementRef, Input, QueryList, ViewChildren } from "@angular/core";
import { coerceBooleanProperty, BooleanInput } from "@vattenfall/util";

import { InputsElementAccessor } from "./inputs-accessor";
import { InputOptionDirective } from "./lists";

/**
 * The `ListsElementAccessor` contains methods and properties shared by all list-like input components.
 * It require a generic type to be specified, this type should correspond to the value emitted on input change.
 */
@Directive()
export class ListsElementAccessor extends InputsElementAccessor<unknown, HTMLSelectElement> implements AfterContentInit {
  @ContentChildren(InputOptionDirective, { descendants: true }) readonly inputOptionDirectives!: QueryList<InputOptionDirective>;
  @ViewChildren("inputOptionElement") protected readonly inputOptionElements!: QueryList<ElementRef<HTMLInputElement>>;
  /**
   * Whether multiple items can be selected. When enabled, the output value of `change` will be an array containing the selected values.
   *
   * Default is `false`.
   */
  @Input()
  get multiple(): boolean {
    return this._multiple;
  }
  set multiple(value: BooleanInput) {
    this._multiple = coerceBooleanProperty(value);
  }
  private _multiple = false;

  /**
   * The labels of the selected options
   */
  get selectedLabels(): Array<{ label: string; sublabel: string }> {
    return this.inputOptionDirectives
      .filter((inputOptionDirective) => coerceBooleanProperty(inputOptionDirective.selected))
      .map((inputOptionDirective) => ({
        label: inputOptionDirective.label ?? "",
        sublabel: inputOptionDirective.sublabel ?? "",
      }));
  }
  /**
   * The values of the selected options
   */
  get selectedValues(): Array<unknown> {
    return this.inputOptionDirectives
      .filter((inputOptionDirective) => coerceBooleanProperty(inputOptionDirective.selected))
      .map((inputOptionDirective) => inputOptionDirective.value);
  }
  /**
   * Overriding base method to ensure that the option is selected when the value is set programmatically
   */
  override writeValue(value: unknown): void {
    this.inputOptionElements.forEach((inputOptionElement) => {
      if (Array.isArray(value)) {
        if (value.includes(inputOptionElement.nativeElement.value)) inputOptionElement.nativeElement.checked = true;
      } else if (inputOptionElement.nativeElement.value == value) inputOptionElement.nativeElement.checked = true;
      else inputOptionElement.nativeElement.checked = false;
    });
    this.setSelectedOptions();
    super.writeValue(value);
  }
  /**
   * Callback function to be invoked every time an option has been selected or deselected.
   */
  onSelectChange() {
    this.setSelectedOptions();
    this.setRequiredValidator();
    // Dispatch array of values if multiple, otherwise dispatch a single value
    if (this.multiple) super.onChange(this.selectedValues);
    else super.onChange(this.selectedValues[0]);
  }

  /**
   * Check list elements if selected and selects them in view
   */
  private setSelectedOptions() {
    this.inputOptionDirectives.forEach((inputOptionDirective, index) => {
      inputOptionDirective.selected = this.inputOptionElements.get(index)?.nativeElement.checked ?? false;
    });
  }
  /**
   * Custom validator to check if multiple list element has a selected value.
   * This functionality is not supported in native select elements.
   */
  private setRequiredValidator() {
    // Putting this at the end of callstack to ensure the options
    // has been rendered in the ngFor loop first
    setTimeout(() => {
      if (this.multiple && this.required) {
        if (this.inputOptionElements.some((inputOptionElement) => inputOptionElement.nativeElement.checked)) {
          this.inputElement.nativeElement.setCustomValidity("");
        } else this.inputElement.nativeElement.setCustomValidity("At least one value must be selected");
        this.changeDetectorRef.markForCheck();
      }
    });
  }
  override ngAfterContentInit() {
    super.ngAfterContentInit();
    // Pass this class to children so that change detection can be invoked when value change programmatically
    this.inputOptionDirectives.forEach((inputListDirective) => (inputListDirective.listsElementAccessor = this));
    this.setRequiredValidator();
  }
}
